import { AttachmentImage } from "@/components/ui/attachment-media";
/**
 * Fullscreen image lightbox, portalled to document.body.
 * Click outside, press Escape, or use the close button to dismiss.
 * Pass `onRemove` to offer removing the image (e.g. a composer attachment)
 * straight from the viewer.
 */

import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { uiMessages } from "@/i18n/messages/ui";
import { Button } from "./button";
import { Icons } from "./icons";
import { AttachmentSyncButton } from "./attachment-sync-button";

interface ImageLightboxProps {
  src: string;
  alt?: string;
  onClose: () => void;
  onRemove?: () => void;
}

export function ImageLightbox({
  src,
  alt,
  onClose,
  onRemove,
}: ImageLightboxProps) {
  const t = useT(uiMessages);
  const tc = useT(commonMessages);
  useEffect(() => {
    // Capture on window so Escape closes only the viewer, not the input bar,
    // popover, or pocket that opened it.
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", handleKey, true);
    return () => window.removeEventListener("keydown", handleKey, true);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      // React events bubble through portals: keep presses in the viewer from
      // reaching the frame, input bar, or popover that opened it.
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      // Ownership markers: receive clicks in click-through desktop windows,
      // skip ancestor capture-phase drag starters, and keep the composer,
      // pocket, and tray that opened the viewer from treating clicks inside it
      // as outside clicks.
      data-interactive="true"
      data-no-drag=""
      data-input-bar-root=""
      data-task-pocket-owned=""
      data-tray-popover="true"
      data-testid="image-lightbox"
    >
      <div
        className="absolute right-4 top-4 flex items-center gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        <AttachmentSyncButton key={src} src={src} />
        {onRemove ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onRemove}
            data-testid="image-lightbox-remove"
          >
            <Icons.Trash2 className="h-3.5 w-3.5" />
            {tc("remove")}
          </Button>
        ) : null}
        <Button
          type="button"
          variant="secondary"
          size="iconSm"
          onClick={onClose}
          aria-label={t("closePreview")}
          data-testid="image-lightbox-close"
        >
          <Icons.X className="h-3.5 w-3.5" />
        </Button>
      </div>
      <AttachmentImage
        src={src}
        alt={alt ?? t("fullSizeImage")}
        className="max-h-[85vh] max-w-[90vw] rounded-preview object-contain shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="image-lightbox-img"
      />
    </div>,
    document.body,
  );
}
